import { useState, useCallback, useRef } from "react";
import { WEBHOOKS } from "../config";

/**
 * useQuestionnaire
 * Handles questionnaire state and n8n communication.
 */
export function useQuestionnaire() {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);

  // Persistent session ID for current questionnaire run
  const sessionIdRef = useRef(null);

  /**
   * Generate new session
   */
  const createSessionId = () => {
    return `sess_${Date.now()}`;
  };

  /**
   * Fetch current question
   */
  const fetchQuestion = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Create session only once
      if (!sessionIdRef.current) {
        sessionIdRef.current = createSessionId();
        console.log("Created session:", sessionIdRef.current);
      }

      const response = await fetch(WEBHOOKS.GET_QUESTION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      console.log("Question response:", data);

      if (!data.success) {
        throw new Error("Server returned an invalid response.");
      }

      if (data.completed) {
        setIsComplete(true);
        setCurrentQuestion(null);
      } else {
        setCurrentQuestion({
          id: data.question_id,
          text: data.question,
        });

        setQuestionIndex(data.current_question_index || 0);
      }
    } catch (err) {
      console.error(err);

      if (err.name === "TypeError") {
        setError(
          "Could not connect to the server. Please check your connection."
        );
      } else {
        setError(err.message || "Something went wrong.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Submit answer
   */
  const submitAnswer = useCallback(
    async (answer) => {
      if (!currentQuestion) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(WEBHOOKS.SUBMIT_ANSWER, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            question_id: currentQuestion.id,
            answer,
          }),
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();

        console.log("Submit response:", data);

        if (!data.success) {
          throw new Error("Answer could not be saved.");
        }

        // Clear stale question before loading next one
        setCurrentQuestion(null);

        // Load next question
        await fetchQuestion();
      } catch (err) {
        console.error(err);

        if (err.name === "TypeError") {
          setError(
            "Could not save your answer. Please check your connection."
          );
        } else {
          setError(err.message || "Failed to save answer.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [currentQuestion, fetchQuestion]
  );

  /**
   * Reset questionnaire
   */
  const reset = useCallback(() => {
    sessionIdRef.current = null;

    setCurrentQuestion(null);
    setQuestionIndex(0);
    setIsComplete(false);
    setError(null);
    setIsLoading(false);
  }, []);

  /**
   * Clear errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    currentQuestion,
    questionIndex,
    isLoading,
    isComplete,
    error,
    fetchQuestion,
    submitAnswer,
    reset,
    clearError,
  };
}